import { Component, inject } from '@angular/core';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DashboardSummary, Token } from './types/embed';
import { AppService } from './services/app.service';
import { environment } from '../environments/environment';
import { LocalStorageHandler } from './helpers/local-storage-handler';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [ReactiveFormsModule],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})
export class AppComponent {
    private appService = inject(AppService);
    private sanitizer = inject(DomSanitizer);

    public showSettings = false;
    public hostTokenInput: FormControl<string>;
    public hotelTokenInput: FormControl<string>;
    public clusterTokenInput: FormControl<string>;
    public dashboardInput: FormControl<string>;
    public token!: Token;
    public dashboardUrl!: SafeResourceUrl;
    public useLightTheme: boolean;
    public isCluster = false;
    public theme = 'dark';
    public dashboardName = '';

    private hostToken: string;
    private hotelToken: string;
    private clusterToken: string;
    private dashboardId: string;

    public dashboards: DashboardSummary[] = [];
    public clusterDashboards: DashboardSummary[] = [];

    constructor() {
        this.useLightTheme = LocalStorageHandler.getTheme() === 'light';
        this.theme = LocalStorageHandler.getTheme() || 'dark';
        this.hostToken = LocalStorageHandler.getHostToken();
        this.hotelToken = LocalStorageHandler.getHotelToken();
        this.clusterToken = LocalStorageHandler.getClusterToken();
        this.dashboardId = LocalStorageHandler.getDashboard();
        this.hostTokenInput = new FormControl(this.hostToken, { nonNullable: true });
        this.hotelTokenInput = new FormControl(this.hotelToken, { nonNullable: true });
        this.clusterTokenInput = new FormControl(this.clusterToken, { nonNullable: true });
        this.dashboardInput = new FormControl(this.dashboardId, { nonNullable: true });

        this.getCanvasDashboards();
        this.getClusterDashboards();
    }

    public onDashboardClick(id: string, isCluster: boolean): void {
        this.dashboardId = id;
        this.isCluster = isCluster;
        this.refreshDashboard();
    }

    public showSettingsPopup(): void {
        this.showSettings = !this.showSettings;
    }

    public closeSettings(): void {
        this.showSettings = false;
    }

    public saveSettings(): void {
        this.hostToken = this.hostTokenInput.value;
        this.hotelToken = this.hotelTokenInput.value;
        this.clusterToken = this.clusterTokenInput.value;
        this.dashboardId = this.dashboardInput.value;
        this.theme = this.useLightTheme ? 'light' : 'dark';
        LocalStorageHandler.setHostToken(this.hostToken);
        LocalStorageHandler.setHotelToken(this.hotelToken);
        LocalStorageHandler.setClusterToken(this.clusterToken);
        LocalStorageHandler.setDashboard(this.dashboardId);
        LocalStorageHandler.setTheme(this.theme);
        if (this.hostToken && this.hotelToken) {
            this.isCluster = false;
            this.getCanvasDashboards();
        }
        if (this.hostToken && this.clusterToken) {
            this.getClusterDashboards();
            if (!this.hotelToken) {
                this.isCluster = true;
            }
        }
        this.refreshDashboard();
        this.closeSettings();
    }

    public toggleTheme(): void {
        this.useLightTheme = !this.useLightTheme;
        this.theme = this.useLightTheme ? 'light' : 'dark';
    }

    private getCanvasDashboards(): void {
        if (this.hostToken && this.hotelToken) {
            this.appService.getCanvasDashboards(this.hostToken, this.hotelToken).subscribe((hotelSummary) => {
                this.dashboards = hotelSummary.dashboards;
                const activeDashboard = this.dashboards[0];
                if (activeDashboard) {
                    this.dashboardId = activeDashboard.id;
                    this.refreshDashboard();
                }
            });
        }
    }

    private getClusterDashboards(): void {
        if (this.hostToken && this.clusterToken) {
            this.appService.getClusterDashboards(this.hostToken, this.clusterToken).subscribe((hotelSummary) => {
                this.clusterDashboards = hotelSummary.dashboards;
            });
        }
    }

    private refreshDashboard(): void {
        if (this.hostToken && (this.hotelToken || this.clusterToken) && this.dashboardId) {
            this.appService.getEmbedToken(this.hostToken, !this.isCluster ? this.hotelToken : this.clusterToken).subscribe(token => {
                const page = this.isCluster ? 'consolidated' : 'dashboard';
                this.dashboardUrl = this.sanitizer.bypassSecurityTrustResourceUrl(`${environment.embed_url}/${page}?dash=${this.dashboardId}`);
                this.token = token;
                const object = document.getElementById('iframe') as HTMLIFrameElement;
                object.onload = () => {
                    object.contentWindow?.postMessage({ token: this.token, theme: this.theme }, environment.embed_url);
                };
                const dashboards = [...(this.dashboards || []), ...(this.clusterDashboards || [])];
                const selected = dashboards.find(d => d.id === this.dashboardId);
                if (selected) {
                    this.dashboardName = selected.name;
                }
            });
        }
    }
}
